import React, {useCallback, useMemo} from 'react'
import {
  ChevronProps,
  Day,
  DayButton,
  DayButtonProps,
  DayPicker,
  DayProps,
  Formatters,
  MonthCaption,
  MonthCaptionProps,
  Weekday,
  WeekdayProps
} from 'react-day-picker'
import {defineMessage, useIntl} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {addMonths} from 'date-fns/addMonths'
import {endOfMonth} from 'date-fns/endOfMonth'
import {enUS} from 'date-fns/locale/en-US'
import {startOfDay} from 'date-fns/startOfDay'
import {startOfMonth} from 'date-fns/startOfMonth'

import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'
import {getDayPriceInfo} from '@vio/core/src/_web/availability'
import {DatePickerVariant, DayOfWeekType} from '@vio/core/src/datePicker/types'
import {formatAbbreviatedPriceValue} from '@vio/core/src/price/business/formatPrice'
import {
  dateFormat,
  dateStringToMiddayDate,
  UTS_DATE_FORMAT
} from '@vio/core/src/utils/date'
import {
  applyRoundingStrategy,
  getRoundingStrategy
} from '@vio/core/src/utils/number'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {ColorPriceDayInner} from '../components'
import {
  MONTHS_TO_SHOW,
  PricePredictionEntry,
  pricesToAvailabilityEntity
} from './helpers'
import {PricePredictionLegend} from './PricePredictionLegend'
import {PricePredictionContainer} from './styles'

export interface Props {
  /** Predicted nightly prices; a price of 0 means no data for that date. */
  prices: PricePredictionEntry[]
  /** Currency for the prices and the legend caption. */
  currency: string
  firstDayOfWeek: number
  weekdaysShort: string[]
  months: string[]
  variant: DatePickerVariant
  /**
   * Overrides "today" for deterministic rendering (tests/SSR). After
   * hydration it also sets the cutoff below which days are disabled.
   */
  referenceDate?: Date
  onDayClick?: (date: Date) => void
}

const DAY_PRICE_MAXIMUM_FRACTION_DIGITS = 1

const dayWithNightlyPriceMessage = defineMessage({
  id: 'datePicker.dayWithNightlyPrice',
  defaultMessage: '{date}, {price} per night'
})

const MonthName = styled.div`
  ${cssTheme.typography.titleS};
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s600};
`
const WeekDay = styled.div`
  display: table-cell;
  text-align: center;
  color: ${cssTheme.colors.content.neutral.secondary};
  ${cssTheme.typography.bodyS};
`

const Wrapper = styled.div`
  width: 100%;

  &.--horizontal {
    border: 1px solid ${cssTheme.colors.border.neutral.secondary};
    border-radius: ${cssTheme.layout.radius.lg};
  }

  &.--vertical {
    padding: 0 ${cssTheme.layout.spacing.s300};
  }
`

const PricePredictionComponent: React.FC<Props> = ({
  prices,
  currency,
  firstDayOfWeek,
  weekdaysShort,
  months,
  variant = 'horizontal',
  referenceDate = new Date(),
  onDayClick = () => {}
}) => {
  const intl = useIntl()
  const isHydrated = useIsHydrated()

  // The API returns prices starting from yesterday because the server can't
  // know the user's timezone. Once hydrated we know the local date, so days
  // before local today lose their price and get disabled. Server markup and
  // the first client render stay untouched to avoid a hydration mismatch.
  const localToday = isHydrated ? startOfDay(referenceDate) : undefined
  const localTodayFormatted =
    localToday && dateFormat(localToday, UTS_DATE_FORMAT)

  const visiblePrices = useMemo(
    () =>
      localTodayFormatted
        ? prices.filter(({checkIn}) => checkIn >= localTodayFormatted)
        : prices,
    [prices, localTodayFormatted]
  )

  const hotelAvailabilityPrices = useMemo(
    () => pricesToAvailabilityEntity(visiblePrices, currency),
    [visiblePrices, currency]
  )

  const getDayPrice = useCallback(
    (date: Date) =>
      getDayPriceInfo(
        hotelAvailabilityPrices,
        dateFormat(date, UTS_DATE_FORMAT)
      ),
    [hotelAvailabilityPrices]
  )

  // Keyed on the formatted day so the Dates keep a stable identity across
  // renders (`referenceDate` defaults to a fresh `new Date()` per render),
  const referenceDay = dateFormat(referenceDate, UTS_DATE_FORMAT)
  const comparisonDateRange = useMemo(() => {
    const referenceMidday = dateStringToMiddayDate(referenceDay)
    return {
      start: startOfMonth(referenceMidday),
      end: endOfMonth(addMonths(referenceMidday, MONTHS_TO_SHOW - 1))
    }
  }, [referenceDay])
  const {start: currentMonth, end: nextMonthEnd} = comparisonDateRange

  const isVertical = variant === 'vertical'

  const monthProps = isVertical
    ? {numberOfMonths: 1, defaultMonth: currentMonth}
    : {
        numberOfMonths: MONTHS_TO_SHOW,
        month: currentMonth,
        hideNavigation: true,
        disableNavigation: true
      }

  // Machine-readable price markers on the gridcell, next to the `data-day` date react-day-picker already emits.
  const CustomDay = useCallback(
    (props: DayProps) => {
      if (props.day.outside) return <Day {...props} />
      const {displayPrice, currency: dayCurrency} = getDayPrice(props.day.date)
      if (!displayPrice) return <Day {...props} />
      return (
        <Day
          {...props}
          // Rounded like the visible price, so the two always match
          data-price={applyRoundingStrategy(
            getRoundingStrategy(),
            displayPrice
          )}
          data-currency={dayCurrency}
        />
      )
    },
    [getDayPrice]
  )

  // The button's aria-label overrides its child text in the accessibility
  // tree, so the price must be part of the label to be announced at all.
  const labelDayButtonWithPrice = useCallback(
    (date: Date) => {
      const dateLabel = intl.formatDate(date, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      const {displayPrice, currency: dayCurrency} = getDayPrice(date)
      if (!displayPrice) return dateLabel
      // Same options as the cell's <Price>, so both render the same text
      return intl.formatMessage(dayWithNightlyPriceMessage, {
        date: dateLabel,
        price: formatAbbreviatedPriceValue({
          locale: intl.locale,
          value: displayPrice,
          currency: dayCurrency,
          roundingStrategy: getRoundingStrategy(),
          useShortCurrency: true,
          maximumFractionDigits: DAY_PRICE_MAXIMUM_FRACTION_DIGITS
        })
      })
    },
    [getDayPrice, intl]
  )

  const CustomDayButton = useCallback(
    (props: DayButtonProps) => (
      <DayButton {...props}>
        <ColorPriceDayInner
          day={props.day.date}
          hotelAvailabilityPrices={hotelAvailabilityPrices}
          searchComplete
          shouldShowPrices
          noUnavailabilityIcon
          maximumFractionDigits={DAY_PRICE_MAXIMUM_FRACTION_DIGITS}
          comparisonDateRange={comparisonDateRange}
        />
      </DayButton>
    ),
    [hotelAvailabilityPrices, comparisonDateRange]
  )

  const CustomMonthCaption = useCallback(
    (props: MonthCaptionProps) => (
      <MonthCaption {...props}>
        <MonthName>
          {months &&
            `${months[props.calendarMonth.date.getMonth()]} ${props.calendarMonth.date.getFullYear()}`}
        </MonthName>
      </MonthCaption>
    ),
    [months]
  )

  const formatWeekdayName: Formatters['formatWeekdayName'] = useCallback(
    (date: Date) => weekdaysShort[date.getDay()],
    [weekdaysShort]
  )

  const CustomWeekday = useCallback((props: WeekdayProps) => {
    return (
      <Weekday {...props}>
        <div className={cx('week-day-inner')}>
          <WeekDay>{props.children}</WeekDay>
        </div>
      </Weekday>
    )
  }, [])

  return (
    <Wrapper className={cx(`--${variant}`)}>
      <PricePredictionContainer
        className={cx(
          variant === 'vertical' && '--is-vertical',
          '--show-week-days'
        )}
      >
        <DayPicker
          locale={enUS}
          today={referenceDate}
          classNames={{
            root: 'wrapper',
            day_button: 'day-cell',
            months: 'months-wrapper',
            month: 'month-wrapper',
            month_caption: 'caption-wrapper',
            caption_label: 'caption-label-wrapper',
            weekday: 'week-day-wrapper',
            month_grid: 'calendar-wrapper',
            weekdays: 'calendar-week-days-wrapper',
            nav: 'nav-wrapper',
            button_next: 'nav-button nav-button-next',
            button_previous: 'nav-button nav-button-previous'
          }}
          startMonth={currentMonth}
          endMonth={nextMonthEnd}
          disabled={localToday && {before: localToday}}
          {...monthProps}
          onDayClick={onDayClick}
          weekStartsOn={firstDayOfWeek as DayOfWeekType}
          formatters={{formatWeekdayName}}
          labels={{labelDayButton: labelDayButtonWithPrice}}
          components={{
            Day: CustomDay,
            DayButton: CustomDayButton,
            MonthCaption: CustomMonthCaption,
            Weekday: CustomWeekday,
            Chevron: ({orientation, disabled}: ChevronProps) => (
              <Icon
                name={orientation === 'left' ? 'ChevronLeft' : 'ChevronRight'}
                colorPath={
                  disabled
                    ? 'content.neutral.disabled'
                    : 'content.brand.primary'
                }
              />
            )
          }}
          modifiersClassNames={{
            today: 'today',
            outside: 'outside',
            disabled: 'disabled'
          }}
        />
      </PricePredictionContainer>
      <PricePredictionLegend currency={currency} variant={variant} />
      <div
        aria-hidden="true"
        style={{display: 'none'}}
        data-id="PricePredictionData"
      >
        <ul>
          {prices.map(item => {
            if (item.price === 0)
              return <li key={item.checkIn}>{item.checkIn}: NO DATA</li>
            return (
              <li key={item.checkIn}>
                {item.checkIn}: {item.price}
                {currency}
              </li>
            )
          })}
        </ul>
      </div>
    </Wrapper>
  )
}

export const PricePrediction = React.memo(PricePredictionComponent)
